import { Box, Link, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import { Button } from "../../components";
import { CustomTable } from "../../components/DataTable";
import { colors } from "../../theme/colors";
import type { SpecimenAuditEvent } from "../../services/api";
import { StatusTransition, TagPill, SeverityPill } from "./components/StatusPill";

/**
 * Full-page timeline for a single specimen, reached from the "View timeline"
 * row action on the Specimen Tracking screen.
 *
 * The audit API exposes no get-event-by-id endpoint, so the originating screen
 * hands the event and its sibling events over in router state. Browsers persist
 * history.state, so this survives a reload — but not a new tab or a shared
 * link, which is why the no-state fallback below sends the user back rather
 * than rendering an empty screen.
 */

type TimelineNavState = {
  event?: SpecimenAuditEvent;
  timeline?: SpecimenAuditEvent[];
  /** The tracking feed was truncated or filtered, so this is not full history. */
  isPartialTimeline?: boolean;
  healthOrgName?: string;
};

// Mirrors the label/value rows in Specimen/specimen.module.css so this screen
// reads like the other details screens without coupling to their stylesheet.
const detailsRowSx = {
  display: "flex",
  gap: "20px",
  mb: "10px",
  alignItems: "flex-start",
} as const;

const titleSx = {
  fontSize: 16,
  color: "#000",
  lineHeight: "25px",
  flex: 0.24,
} as const;

const valueSx = {
  fontSize: 16,
  color: "#171717",
  lineHeight: "25px",
  fontWeight: 600,
  flex: 0.76,
  minWidth: 0,
} as const;

const sectionHeadingSx = {
  fontSize: 18,
  fontWeight: 500,
  lineHeight: "36px",
  mt: 3,
  mb: 1,
} as const;

const entityLabel: Record<string, string> = {
  specimen: "Specimen",
  hazard_bag: "Hazard bag",
  request_form: "Request form",
};

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format("DD/MM/YYYY hh:mm A") : "—";

// The per-specimen list often holds several events inside one minute, so this
// screen keeps the seconds that the feed's minute-resolution column drops.
const formatDateTimeWithSeconds = (value?: string | null) =>
  value ? dayjs(value).format("DD/MM/YYYY hh:mm:ss A") : "—";

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Box sx={detailsRowSx}>
    <Typography sx={titleSx}>{label}</Typography>
    <Box sx={valueSx}>{children}</Box>
  </Box>
);

const SpecimenTimelineDetails = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { event, timeline, isPartialTimeline, healthOrgName } = (state ??
    {}) as TimelineNavState;

  const goBack = () => navigate("/specimen-tracking");

  if (!event) {
    return (
      <Box
        sx={{
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          p: 4,
          mt: "5px",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>
          This timeline is no longer available
        </Typography>
        <Typography sx={{ color: colors.grey, textAlign: "center" }}>
          Timeline details are carried over from the tracking screen, so this
          page cannot be opened directly or shared as a link. Open it again from
          Specimen Tracking.
        </Typography>
        <Button
          variant="contained"
          label="Back to Specimen Tracking"
          onClick={goBack}
          sx={{ borderRadius: "8px", height: 48, px: 3 }}
        />
      </Box>
    );
  }

  const events = timeline?.length ? timeline : [event];
  const impacts = event.impacts ?? [];

  const fields = [
    {
      id: "occurred_at",
      label: "Time",
      flex: 1.8,
      render: (row: SpecimenAuditEvent) => (
        <Typography sx={{ fontSize: 15, color: colors.lighterBlack }}>
          {formatDateTimeWithSeconds(row.occurred_at)}
        </Typography>
      ),
    },
    {
      id: "transition",
      label: "Transition",
      flex: 2.4,
      render: (row: SpecimenAuditEvent) => (
        <StatusTransition
          from={row.status_transition_from}
          to={row.status_transition_to}
        />
      ),
    },
    {
      // Without this, two events recorded in the same minute that left the
      // status untouched (CREATED then PRINTED) render as identical rows.
      id: "action",
      label: "Action",
      flex: 1.2,
      render: (row: SpecimenAuditEvent) => (
        <Typography sx={{ fontSize: 15, color: colors.lighterBlack }}>
          {row.action || "—"}
        </Typography>
      ),
    },
    {
      id: "actor_email",
      label: "Actor",
      flex: 1.8,
      render: (row: SpecimenAuditEvent) => (
        <Typography
          sx={{
            fontSize: 15,
            color: colors.lighterBlack,
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {row.actor_email || "—"}
        </Typography>
      ),
    },
    {
      id: "severity",
      label: "Severity",
      type: "badge",
      flex: 1,
      render: (row: SpecimenAuditEvent) => (
        <SeverityPill severity={row.severity} />
      ),
    },
  ];

  return (
    <Box sx={{ mt: 2 }}>
      <Box
        sx={{
          border: `1px solid ${colors.grey}`,
          borderRadius: "4px",
          p: "20px",
        }}
      >
        {healthOrgName && (
          <DetailRow label="Health Organisation">
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              {healthOrgName}
            </Typography>
          </DetailRow>
        )}
        <DetailRow label="Specimen QR">
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {event.specimen_qr || "—"}
          </Typography>
        </DetailRow>
        <DetailRow label="Bag QR">
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {event.bag_qr || "—"}
          </Typography>
        </DetailRow>
        <DetailRow label="Request Form">
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {event.request_form_id ? `RF-${event.request_form_id}` : "—"}
          </Typography>
        </DetailRow>
        <DetailRow label="Recorded At">
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {formatDateTime(event.occurred_at)}
          </Typography>
        </DetailRow>
        <DetailRow label="Actor">
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
            {event.actor_email || "—"}
          </Typography>
        </DetailRow>
        <DetailRow label="Transition">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              flexWrap: "wrap",
            }}
          >
            <StatusTransition
              from={event.status_transition_from}
              to={event.status_transition_to}
            />
            <SeverityPill severity={event.severity} />
            {event.is_hidden_recalculation && (
              <TagPill
                label="Recalculated"
                bg={colors.modalBackground}
                color="#9A3412"
              />
            )}
          </Box>
        </DetailRow>
        {(event.reason || event.comments) && (
          <DetailRow label="Reason / Comments">
            <Typography sx={{ fontSize: 16, fontWeight: 600 }}>
              {[event.reason, event.comments].filter(Boolean).join(" · ")}
            </Typography>
          </DetailRow>
        )}
        {event.evidence_url && (
          <DetailRow label="Evidence">
            <Link
              href={event.evidence_url}
              target="_blank"
              rel="noreferrer"
              sx={{ fontSize: 16, color: colors.primary, fontWeight: 500 }}
            >
              View evidence
            </Link>
          </DetailRow>
        )}

        <Typography sx={sectionHeadingSx}>Behind-the-scenes impact</Typography>
        {impacts.length ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {impacts.map((impact, index) => (
              <Box
                key={`${impact.entity}-${index}`}
                data-testid={`impact-${impact.entity}`}
                sx={{
                  border: `1px solid ${colors.lighterGrey}`,
                  borderRadius: "8px",
                  backgroundColor: colors.lightestGrey,
                  p: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                  {entityLabel[impact.entity] ?? impact.entity}
                </Typography>
                <Box sx={{ display: "flex", mt: 0.75 }}>
                  <StatusTransition
                    from={impact.previous_status}
                    to={impact.current_status}
                  />
                </Box>
                {impact.action && (
                  <Typography
                    sx={{ fontSize: 12, color: colors.grey, mt: 0.75 }}
                  >
                    Action: {impact.action}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        ) : (
          <Typography sx={{ fontSize: 14, color: colors.grey }}>
            No impact records for this event.
          </Typography>
        )}

        <Typography sx={sectionHeadingSx}>
          {!event.specimen_qr
            ? "Selected event"
            : isPartialTimeline
              ? `Activity for this specimen (${events.length} ${events.length === 1 ? "event" : "events"
              } from the current view)`
              : "All activity for this specimen"}
        </Typography>
        {!event.specimen_qr && (
          <Typography sx={{ fontSize: 14, color: colors.grey, mb: 1 }}>
            This event is not linked to a specimen QR, so no wider timeline is
            available.
          </Typography>
        )}
        {event.specimen_qr && isPartialTimeline && (
          <Typography sx={{ fontSize: 14, color: colors.grey, mb: 1 }}>
            Limited to the events loaded on the tracking screen under its current
            filters — this specimen may have further history. Clear the filters
            there to widen the view.
          </Typography>
        )}
        <Box sx={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
          <CustomTable<SpecimenAuditEvent>
            fields={fields}
            data={events}
            paginationRequired={false}
            defaultOrderBy="occurred_at"
            defaultOrder="Dsc"
          />
        </Box>
      </Box>
    </Box>
  );
};

export default SpecimenTimelineDetails;
